'use client';

import { PINNED_TOP_HEIGHT_VAR } from '@hyphengroup/cms-components/utils/pinnedHeight';
import classNames from 'classnames';
import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

type GlobalStickyPanelProps = {
  /**
   * Enables the pin-to-viewport behaviour. When false the children render in
   * normal flow with no sticky/fixed handling (markup identical to a plain div).
   */
  enabled: boolean;
  /**
   * Forces the un-pinned (in-flow) state while keeping the enabled machinery and
   * children mounted — used by the sticky top-slot arbiter so only one element
   * pins at the top (CMS-1643 AC-13). Unlike toggling `enabled` (which swaps to
   * the plain-div branch and remounts the children), this only forces
   * `pinned = false`, so the container-reuse design avoids any remount on
   * handoff.
   */
  suspended?: boolean;
  /** Notified whenever the pinned state changes (for the top-slot arbiter). */
  onPinnedChange?: (pinned: boolean) => void;
  /** Distance from the top of the viewport the panel pins at, in px. */
  offsetPx?: number;
  /** Classes applied to the panel wrapper in every state. */
  className?: string;
  /** Extra classes applied only while the panel is pinned (e.g. background). */
  pinnedClassName?: string;
  children: ReactNode;
  // Forwarded onto the panel root in both states so a caller can mark this
  // subtree (e.g. `data-component`) without adding a node inside a portalled,
  // measured container (CMS-1878).
} & { [dataAttr: `data-${string}`]: string | number | boolean | undefined };

/**
 * Pins its children to the top of the viewport once they scroll past it,
 * independently of where the component sits in the DOM tree.
 *
 * `position: sticky` only works within the nearest scroll container and its
 * direct containing block, so a panel nested inside a short wrapper (here the
 * trip-details panel lives in a wrapper that ends right after it, while the
 * results are sibling blocks) un-pins as soon as that wrapper scrolls away. Any
 * ancestor with `overflow` / `transform` / `filter` / `contain` breaks it too.
 *
 * To escape those constraints the pinned panel is portalled to `document.body`
 * and switched to `position: fixed`. A zero-jump placeholder reserves its space
 * in flow. While pinned the panel spans the full viewport width so its
 * background bleeds edge-to-edge, and its content is inset back to the content
 * column with horizontal padding derived from the in-flow sentinel's rect (the
 * sentinel spans the column and never shifts horizontally on scroll). The panel
 * renders in normal flow until pinned, so SSR/hydration markup is unchanged.
 *
 * The portal target is a single container created the first time the panel
 * pins, then kept for the component's lifetime. Every later pin/unpin only
 * moves that same container between the in-flow anchor and `document.body`
 * via plain DOM `appendChild` — a container swap React's portal reconciliation
 * never sees, since `createPortal` is always called with the same container
 * reference. Toggling between an unportalled div and a portalled one (the
 * naive approach) changes the element type at that position on every pin/unpin
 * and makes React unmount and remount everything inside, so only the very
 * first pin — which creates the container — pays that cost.
 */
export const GlobalStickyPanel = ({
  enabled,
  suspended = false,
  onPinnedChange,
  offsetPx = 0,
  className,
  pinnedClassName,
  children,
  ...rest
}: GlobalStickyPanelProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  const [isStuck, setIsStuck] = useState(false);
  const [metrics, setMetrics] = useState<{
    left: number;
    width: number;
    bodyWidth: number;
  } | null>(null);
  const [panelHeight, setPanelHeight] = useState(0);

  // Stuck detection: the sentinel is a zero-height marker in normal flow right
  // before the panel. It counts as stuck only once it has scrolled above the
  // pin line (top < offsetPx) — a sentinel still below the fold never triggers.
  useEffect(() => {
    if (!enabled) {
      setIsStuck(false);
      return;
    }
    const node = sentinelRef.current;
    if (!node) return;
    // Graceful no-op where IntersectionObserver is absent (e.g. jsdom without a
    // polyfill); the panel simply stays in flow.
    if (!('IntersectionObserver' in window)) return;

    const isStuckAt = (top: number) => top < offsetPx;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(isStuckAt(entry.boundingClientRect.top)),
      { threshold: 0, rootMargin: `${-offsetPx}px 0px 0px 0px` },
    );
    observer.observe(node);

    // The observer fires only when the sentinel crosses the view, and a jump
    // (Home, the scrollbar, an anchor) can skip that.
    const recheck = () =>
      setIsStuck(isStuckAt(node.getBoundingClientRect().top));
    window.addEventListener('scroll', recheck, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', recheck);
    };
  }, [enabled, offsetPx]);

  // The pinned panel spans the full viewport width (so its background bleeds
  // edge-to-edge) while its content is inset back to the content column via
  // horizontal padding. The sentinel spans the column and never moves
  // horizontally on scroll, so its rect drives that inset; bodyWidth excludes
  // the scrollbar (same as PortalPlace) to avoid horizontal overflow. Recompute
  // on window resize and on any resize of the sentinel itself — the latter also
  // catches column-width changes a window resize wouldn't fire for (scrollbar
  // appearing/disappearing, webfont swap, zoom).
  useEffect(() => {
    if (!enabled) return;
    const node = sentinelRef.current;
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      setMetrics({
        left: rect.left,
        width: rect.width,
        bodyWidth: document.body.clientWidth,
      });
    };
    measure();
    window.addEventListener('resize', measure);

    let resizeObserver: ResizeObserver | null = null;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(node);
    }

    return () => {
      window.removeEventListener('resize', measure);
      resizeObserver?.disconnect();
    };
  }, [enabled]);

  // Track the live panel height (in either state) so the placeholder reserves
  // the exact space the panel vacates when it goes fixed — no layout jump.
  const setPanelNode = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    if (!node) return;
    if (!('ResizeObserver' in window)) {
      setPanelHeight(node.offsetHeight);
      return;
    }
    const observer = new ResizeObserver(() =>
      setPanelHeight(node.offsetHeight),
    );
    observer.observe(node);
    resizeObserverRef.current = observer;
    setPanelHeight(node.offsetHeight);
  }, []);

  useEffect(() => () => resizeObserverRef.current?.disconnect(), []);

  // `suspended` forces the un-pinned state (the top-slot arbiter hands the top
  // to another panel) without unmounting children.
  const pinned = isStuck && metrics !== null && !suspended;

  // Anything else pinning to the same line has to clear this panel. The sort-and-filter rail
  // is sticky at the top of the results column and would otherwise sit underneath it
  // (CMS-1542). Published rather than passed down: the rail is several blocks away.
  useEffect(() => {
    const el = document.documentElement;
    el.style.setProperty(
      PINNED_TOP_HEIGHT_VAR,
      pinned ? `${panelHeight + offsetPx}px` : '0px',
    );
    return () => {
      el.style.removeProperty(PINNED_TOP_HEIGHT_VAR);
    };
  }, [pinned, panelHeight, offsetPx]);

  // Report pin transitions upward so the top-slot arbiter can suspend the other
  // panel (CMS-1643 AC-13).
  useEffect(() => {
    onPinnedChange?.(pinned);
  }, [pinned, onPinnedChange]);

  // Created once, the first time the panel pins; reused for every later toggle.
  if (pinned && !portalContainerRef.current) {
    portalContainerRef.current = document.createElement('div');
  }

  // Moves the (already-created) container between its two possible parents.
  // Runs before paint, so the container is never visible mid-move. Refocuses
  // explicitly afterwards — moving a focused element to a new parent isn't
  // guaranteed to keep it focused everywhere.
  useLayoutEffect(() => {
    const container = portalContainerRef.current;
    if (!container) return;
    const target = pinned ? document.body : anchorRef.current;
    if (target && container.parentNode !== target) {
      const { activeElement } = document;
      const hadFocusInside =
        activeElement instanceof HTMLElement &&
        container.contains(activeElement);
      target.appendChild(container);
      if (hadFocusInside) {
        activeElement.focus({ preventScroll: true });
      }
    }
  }, [pinned]);

  useEffect(() => {
    return () => {
      portalContainerRef.current?.remove();
    };
  }, []);

  if (!enabled) {
    return (
      <div className={className} {...rest}>
        {children}
      </div>
    );
  }

  const panel = (
    <div
      ref={setPanelNode}
      {...rest}
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className={classNames(
        className,
        pinned && pinnedClassName,
        pinned && 'fixed z-[var(--z-index-personal-filter)]',
      )}
      style={
        pinned
          ? {
              top: offsetPx,
              left: 0,
              // Full-bleed background; content is inset back to the column.
              width: metrics.bodyWidth,
              paddingLeft: metrics.left,
              paddingRight: metrics.bodyWidth - metrics.left - metrics.width,
            }
          : undefined
      }
    >
      {children}
    </div>
  );

  return (
    <>
      <div ref={sentinelRef} aria-hidden className="h-0" />
      {/* Reserve the vacated space so content below doesn't jump on pin. */}
      <div aria-hidden style={{ height: pinned ? panelHeight : 0 }} />
      <div ref={anchorRef} />
      {portalContainerRef.current
        ? createPortal(panel, portalContainerRef.current)
        : panel}
    </>
  );
};
